import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { hasPermission, RESELLER_LOGO_MAX_BYTES, RESELLER_LOGO_MIME_TYPES, type Role } from "@vital/shared";
import { useAuth, useActiveOrganization } from "../context/AuthContext";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/Card";
import { MetricCard } from "../components/MetricCard";
import { OpsEmptyState, InventorySkeleton } from "../components/ops-display";
import { InspectPanel } from "../components/InspectPanel";
import { LtrIsolate } from "../components/LtrIsolate";
import { StickyFormActions } from "../components/StickyFormActions";
import {
  ComposerHeader,
  ComposerPanel,
  ComposerSection,
  ComposerWorkspace,
} from "../components/ComposerChrome";
import {
  ResellerFilterBar,
  ResellerInspectSummary,
  ResellerInventoryList,
  ResellerLogoThumb,
} from "../components/reseller-inventory";
import {
  api,
  ApiError,
  type ResellerProfileDetail,
  type ResellerProfileSnapshot,
  type ResellerProfileSummary,
} from "../lib/api";
import { useInspectSearchParam } from "../lib/use-inspect-search-param";
import { PasswordConfirmModal } from "../components/PasswordConfirmModal";
import { formatKabulDisplay } from "../components/ScheduleFields";
import { cmsStrings } from "../terminology";
import {
  RESELLER_PAGE_LABEL,
  RESELLER_PAGE_NOTE,
  RESELLER_STATE_LABELS,
  filterAndSortResellerProfiles,
  mapResellerLogoApiError,
  resellerLogoClientValidationError,
  type ResellerSortKey,
} from "../lib/reseller-ops";

type SiteOption = { id: string; code: string; name: string; active?: boolean };

const STATE_LABELS = RESELLER_STATE_LABELS;

export function ResellerProfilesPage() {
  const { user } = useAuth();
  const activeOrg = useActiveOrganization();
  const organizationLabel =
    activeOrg.code || activeOrg.displayName || activeOrg.organizationId || "—";
  const role = user?.role as Role | undefined;
  const canRead = role ? hasPermission(role, "reseller.profile.read") : false;
  const canManage = role ? hasPermission(role, "reseller.profile.manage") : false;
  const canPublish = role ? hasPermission(role, "reseller.profile.publish") : false;
  const canAssign = role ? hasPermission(role, "reseller.site.assign") : false;

  const ui = cmsStrings.resellerUi;
  const c = cmsStrings.resellerComposer;

  const [items, setItems] = useState<ResellerProfileSummary[]>([]);
  const [sites, setSites] = useState<SiteOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [clientFilter, setClientFilter] = useState("all");
  const [sort, setSort] = useState<ResellerSortKey>("name");

  const [selectedId, setSelectedId] = useInspectSearchParam();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [version, setVersion] = useState(1);
  const [code, setCode] = useState("");
  const [operationalName, setOperationalName] = useState("");
  const [supportPhone, setSupportPhone] = useState("");
  const [supportHours, setSupportHours] = useState("");
  const [supportText, setSupportText] = useState("");
  const [tagline, setTagline] = useState("");
  const [termsCompanyName, setTermsCompanyName] = useState("");
  const [logoPath, setLogoPath] = useState<string | null>(null);
  const [hasStagingLogo, setHasStagingLogo] = useState(false);
  const [profileState, setProfileState] = useState<string>("draft");
  const [selectedSiteIds, setSelectedSiteIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [assignBusy, setAssignBusy] = useState(false);

  const [confirmAction, setConfirmAction] = useState<"publish" | "archive" | null>(null);
  const [actionPending, setActionPending] = useState(false);

  const [snapshotsOpen, setSnapshotsOpen] = useState(false);
  const [snapshots, setSnapshots] = useState<ResellerProfileSnapshot[]>([]);
  const [snapshotsLoading, setSnapshotsLoading] = useState(false);
  const [rollbackTarget, setRollbackTarget] = useState<ResellerProfileSnapshot | null>(null);
  const [rollbackPassword, setRollbackPassword] = useState("");
  const [rollbackBusy, setRollbackBusy] = useState(false);

  const codeLocked = profileState === "published" || profileState === "archived";
  const formEditable = canManage && profileState !== "archived";

  const resetForm = () => {
    setEditingId(null);
    setVersion(1);
    setCode("");
    setOperationalName("");
    setSupportPhone("");
    setSupportHours("");
    setSupportText("");
    setTagline("");
    setTermsCompanyName("");
    setLogoPath(null);
    setHasStagingLogo(false);
    setProfileState("draft");
    setSelectedSiteIds([]);
    setConfirmAction(null);
    setConflict(null);
  };

  const loadProfileIntoForm = (p: ResellerProfileSummary | ResellerProfileDetail) => {
    setEditingId(p.id);
    setVersion(p.version);
    setCode(p.code);
    setOperationalName(p.operationalName);
    setSupportPhone(p.supportPhone ?? p.whatsappE164 ?? "");
    setSupportHours(p.supportHours ?? "");
    setSupportText(p.supportText ?? "");
    setTagline(p.tagline ?? "");
    setTermsCompanyName(p.termsCompanyName ?? "");
    setLogoPath(p.logoPath);
    setHasStagingLogo(p.hasStagingLogo);
    setProfileState(p.state);
    setSelectedSiteIds(p.linkedSiteIds);
    setConfirmAction(null);
    setConflict(null);
  };

  const scrollToComposer = () => {
    document.getElementById("reseller-profile-editor")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const scrollToLifecycle = () => {
    document.getElementById("reseller-section-lifecycle")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const loadData = useCallback(async () => {
    if (!canRead) return;
    setLoading(true);
    setError(null);
    try {
      const [listRes, siteList] = await Promise.all([
        api.listResellerProfiles({
          q: search.trim() || undefined,
          state: stateFilter || undefined,
        }),
        api.listSites(),
      ]);
      setItems(listRes.items);
      setSites(siteList);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "بارگذاری ناموفق");
    } finally {
      setLoading(false);
    }
  }, [canRead, search, stateFilter]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const activeSites = useMemo(() => sites.filter((s) => s.active !== false), [sites]);

  const filtered = useMemo(
    () => filterAndSortResellerProfiles(items, { clientFilter, sort }),
    [items, clientFilter, sort],
  );

  const selectedSummary = useMemo(
    () => items.find((i) => i.id === selectedId) ?? null,
    [items, selectedId],
  );

  const draftCount = useMemo(
    () => items.filter((i) => i.state === "draft").length,
    [items],
  );
  const publishedCount = useMemo(
    () => items.filter((i) => i.state === "published").length,
    [items],
  );
  const archivedCount = useMemo(
    () => items.filter((i) => i.state === "archived").length,
    [items],
  );

  useEffect(() => {
    if (loading) return;
    if (selectedId && !items.some((i) => i.id === selectedId)) {
      setSelectedId(null);
    }
  }, [items, loading, selectedId, setSelectedId]);

  useEffect(() => {
    if (!selectedId) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      if (snapshotsOpen || rollbackTarget || confirmAction) return;
      e.preventDefault();
      setSelectedId(null);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedId, snapshotsOpen, rollbackTarget, confirmAction]);

  function toggleSite(siteId: string) {
    setSelectedSiteIds((prev) =>
      prev.includes(siteId) ? prev.filter((id) => id !== siteId) : [...prev, siteId],
    );
  }

  async function openSnapshots(profileId?: string) {
    const id = profileId ?? editingId ?? selectedId;
    if (!id) return;
    setSnapshotsOpen(true);
    setSnapshotsLoading(true);
    setError(null);
    try {
      const res = await api.listResellerProfileSnapshots(id);
      setSnapshots(res.items);
    } catch (err) {
      setSnapshotsOpen(false);
      setError(err instanceof ApiError ? err.message : "بارگذاری اسنپ‌شات ناموفق");
    } finally {
      setSnapshotsLoading(false);
    }
  }

  async function handleRollback(e: FormEvent) {
    e.preventDefault();
    if (!editingId || !rollbackTarget) return;
    setRollbackBusy(true);
    setError(null);
    try {
      const restored = await api.rollbackResellerProfile(
        editingId,
        rollbackTarget.id,
        rollbackPassword,
      );
      loadProfileIntoForm(restored);
      setRollbackTarget(null);
      setRollbackPassword("");
      setSuccess(`بازگشت به نسخه ${rollbackTarget.version} انجام شد (نسخهٔ جدید ثبت شد).`);
      const res = await api.listResellerProfileSnapshots(editingId);
      setSnapshots(res.items);
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "بازگشت ناموفق");
    } finally {
      setRollbackBusy(false);
    }
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!canManage) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    setConflict(null);
    try {
      if (editingId) {
        const updated = await api.updateResellerProfile(editingId, {
          version,
          operationalName,
          supportPhone: supportPhone.trim() || null,
          supportHours: supportHours.trim() || null,
          supportText: supportText.trim() || null,
          tagline: tagline.trim() || null,
          termsCompanyName: termsCompanyName.trim() || null,
        });
        loadProfileIntoForm(updated);
        setSelectedId(updated.id);
        setSuccess("پروفایل به‌روزرسانی شد.");
      } else {
        const created = await api.createResellerProfile({
          code,
          operationalName,
          supportPhone: supportPhone.trim() || null,
          supportHours: supportHours.trim() || null,
          supportText: supportText.trim() || null,
          tagline: tagline.trim() || null,
          termsCompanyName: termsCompanyName.trim() || null,
        });
        loadProfileIntoForm(created);
        setSelectedId(created.id);
        setSuccess("پروفایل ایجاد شد.");
      }
      await loadData();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setConflict(
          "نسخهٔ پروفایل تغییر کرده است. لطفاً دوباره از فهرست بارگذاری کنید و سپس ویرایش کنید.",
        );
        if (err.body?.currentVersion) setVersion(err.body.currentVersion);
      } else {
        setError(err instanceof ApiError ? err.message : "ذخیره ناموفق");
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleLogoUpload(file: File | null) {
    if (!file || !editingId || !canManage) return;
    setLogoBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const clientKey = resellerLogoClientValidationError(file, {
        maxBytes: RESELLER_LOGO_MAX_BYTES,
        allowedMime: RESELLER_LOGO_MIME_TYPES,
      });
      if (clientKey) {
        setError(c[clientKey]);
        return;
      }
      const updated = await api.uploadResellerLogo(editingId, file);
      loadProfileIntoForm(updated);
      setSuccess(
        updated.state === "published"
          ? "لوگو به‌روزرسانی و منتشر شد."
          : "لوگو در فضای موقت ذخیره شد؛ با انتشار عمومی می‌شود.",
      );
      await loadData();
    } catch (err) {
      if (err instanceof ApiError) {
        const key = mapResellerLogoApiError(err.message);
        setError(key ? c[key] : err.message || c.logoErrGeneric);
      } else {
        setError(c.logoErrGeneric);
      }
    } finally {
      setLogoBusy(false);
    }
  }

  async function handleLogoRemove() {
    if (!editingId || !canManage) return;
    if (!window.confirm("لوگو حذف شود؟")) return;
    setLogoBusy(true);
    setError(null);
    try {
      const updated = await api.removeResellerLogo(editingId);
      loadProfileIntoForm(updated);
      setSuccess("لوگو حذف شد.");
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "حذف لوگو ناموفق");
    } finally {
      setLogoBusy(false);
    }
  }

  async function runConfirmAction() {
    if (!editingId || !confirmAction) return;
    setActionPending(true);
    setError(null);
    setSuccess(null);
    try {
      const updated =
        confirmAction === "publish"
          ? await api.publishResellerProfile(editingId)
          : await api.archiveResellerProfile(editingId);
      loadProfileIntoForm(updated);
      setSuccess(confirmAction === "publish" ? "پروفایل منتشر شد." : "پروفایل بایگانی شد.");
      setConfirmAction(null);
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "عملیات ناموفق");
    } finally {
      setActionPending(false);
    }
  }

  async function handleAssignSites() {
    if (!editingId || !canAssign) return;
    setAssignBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await api.setResellerProfileSites(editingId, selectedSiteIds);
      loadProfileIntoForm(updated);
      setSuccess("سایت‌های مرتبط به‌روزرسانی شد. نیازی به ZIP یا آپلود روتر نیست.");
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "اتصال سایت‌ها ناموفق");
    } finally {
      setAssignBusy(false);
    }
  }

  function openInspect(item: ResellerProfileSummary) {
    setSelectedId(item.id);
  }

  function openEditor(item: ResellerProfileSummary) {
    setSelectedId(item.id);
    loadProfileIntoForm(item);
    scrollToComposer();
  }

  function renderResellerInspectFooter(item: ResellerProfileSummary) {
    const tools: ReactNode[] = [];
    const primary: ReactNode[] = [];

    if (canRead) {
      tools.push(
        <button
          key="snapshots"
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => {
            setSelectedId(item.id);
            if (editingId !== item.id) loadProfileIntoForm(item);
            void openSnapshots(item.id);
          }}
        >
          {c.openSnapshots}
        </button>,
      );
    }

    if (canManage && item.state !== "archived") {
      tools.push(
        <button
          key="archive"
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => {
            setSelectedId(item.id);
            loadProfileIntoForm(item);
            setConfirmAction("archive");
            scrollToLifecycle();
          }}
        >
          {c.archive}
        </button>,
      );
    }

    primary.push(
      <button
        key="edit"
        type="button"
        className="btn btn--primary btn--sm"
        onClick={() => openEditor(item)}
      >
        {canManage && item.state !== "archived" ? c.continueEdit : c.viewForm}
      </button>,
    );

    return (
      <div className="inspect-panel__footer-inner">
        {tools.length > 0 ? <div className="inspect-panel__footer-tools">{tools}</div> : null}
        {primary.length > 0 ? <div className="inspect-panel__footer-primary">{primary}</div> : null}
      </div>
    );
  }

  if (!canRead) {
    return (
      <>
        <PageHeader title={RESELLER_PAGE_LABEL} />
        <p className="banner banner--error" role="alert">
          {ui.noAccessBody}
        </p>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={RESELLER_PAGE_LABEL}
        actions={
          canManage ? (
            <a
              href="#reseller-profile-editor"
              className="btn btn--primary btn--sm"
              onClick={() => resetForm()}
            >
              {c.createCta}
            </a>
          ) : undefined
        }
      />

      <aside
        className="ops-safety-summary ops-safety-summary--privacy"
        role="note"
        data-vt-reseller-safety
      >
        <p className="ops-safety-summary__eyebrow">{ui.safetyEyebrow}</p>
        <ul className="ops-safety-summary__points">
          <li>برندینگ مشترک چند سایت — بدون تغییر ZIP، Bundle، یا RouterOS</li>
          <li>بدون تصویر خارجی / CDN؛ لوگو با fallback محلی ایمن</li>
        </ul>
        <details className="ops-safety-disclosure">
          <summary>{ui.safetyDisclosure}</summary>
          <p>{RESELLER_PAGE_NOTE}</p>
        </details>
      </aside>

      {error && (
        <p className="banner banner--error" role="alert">
          {error}
        </p>
      )}
      {success && (
        <p className="banner banner--success" role="status">
          {success}
        </p>
      )}
      {conflict && (
        <p className="banner banner--error" role="alert">
          {conflict}
        </p>
      )}

      <div className="metric-strip" data-vt-inventory-metrics aria-label={ui.listTitle}>
        <MetricCard label={ui.filterAll} value={items.length} loading={loading} />
        <MetricCard label={ui.metricDraft} value={draftCount} loading={loading} />
        <MetricCard
          label={ui.metricPublished}
          value={publishedCount}
          loading={loading}
          sublabel={
            archivedCount > 0
              ? `${ui.metricArchivedSublabel}: ${archivedCount}`
              : undefined
          }
        />
      </div>

      <div className="page-stack page-stack--inventory">
        <div
          className={`content-ops-layout${selectedSummary ? " content-ops-layout--inspect-open" : ""}`}
          data-vt-content-ops
          data-vt-inspect-open={selectedSummary ? "true" : "false"}
        >
          <Card
            className="content-ops-layout__list inventory-surface"
            bodyClassName="card__body--flush"
          >
            <ResellerFilterBar
              search={search}
              onSearchChange={setSearch}
              stateFilter={stateFilter}
              onStateFilterChange={setStateFilter}
              clientFilter={clientFilter}
              onClientFilterChange={setClientFilter}
              sort={sort}
              onSortChange={setSort}
              organizationLabel={organizationLabel}
            />
            {loading ? (
              <InventorySkeleton />
            ) : items.length === 0 ? (
              <OpsEmptyState title="پروفایلی یافت نشد" />
            ) : filtered.length === 0 ? (
              <OpsEmptyState title={cmsStrings.inventory.noMatchTitle} />
            ) : (
              <ResellerInventoryList
                rows={filtered}
                selectedId={selectedId}
                canManage={canManage}
                onSelect={openInspect}
                onEdit={openEditor}
              />
            )}
          </Card>

          {selectedSummary && (
            <InspectPanel
              title={ui.inspectTitle}
              className="content-inspect-panel"
              onClose={() => setSelectedId(null)}
              closeLabel={c.close}
              footer={renderResellerInspectFooter(selectedSummary)}
            >
              <ResellerInspectSummary
                item={selectedSummary}
                organizationLabel={organizationLabel}
              />
            </InspectPanel>
          )}
        </div>

        <ComposerWorkspace id="reseller-profile-editor" layout="solo">
          <ComposerPanel>
            <ComposerHeader
              kind={cmsStrings.composerChrome.kindReseller}
              title={editingId ? c.titleEdit : c.titleCreate}
              statusLabel={
                editingId
                  ? `${STATE_LABELS[profileState] ?? profileState} · نسخه ${version}`
                  : c.statusNew
              }
              statusTone={
                profileState === "published"
                  ? "success"
                  : profileState === "archived"
                    ? "neutral"
                    : "info"
              }
              meta={
                <>
                  سازمان فعال: <LtrIsolate mono>{organizationLabel}</LtrIsolate>
                </>
              }
            />

            <form
              className="content-form content-form--in-card composer-form composer-form--reseller"
              onSubmit={handleSave}
            >
              <ComposerSection
                id="reseller-section-identity"
                title={c.identitySection}
                bare
              >
                <div className="composer-fields-row">
                  <label>
                    <span>{c.codeLabel}</span>
                    <input
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      required
                      disabled={!canManage || Boolean(editingId && codeLocked)}
                      dir="ltr"
                      className="ltr-isolate"
                      pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
                      minLength={3}
                      maxLength={64}
                      placeholder="reseller-herat-01"
                    />
                  </label>
                  <label>
                    <span>{c.nameLabel}</span>
                    <input
                      value={operationalName}
                      onChange={(e) => setOperationalName(e.target.value)}
                      required
                      disabled={!formEditable}
                      maxLength={80}
                    />
                  </label>
                </div>
                <div className="composer-fields-row">
                  <label>
                    <span>{c.phoneLabel}</span>
                    <input
                      value={supportPhone}
                      onChange={(e) => setSupportPhone(e.target.value)}
                      disabled={!formEditable}
                      dir="ltr"
                      className="ltr-isolate"
                      inputMode="tel"
                      placeholder="9370…"
                      maxLength={32}
                    />
                  </label>
                  <p className="form-hint">{c.phoneHint}</p>
                </div>
                <div className="composer-fields-row">
                  <label>
                    <span>{c.hoursLabel}</span>
                    <input
                      value={supportHours}
                      onChange={(e) => setSupportHours(e.target.value)}
                      disabled={!formEditable}
                      maxLength={80}
                    />
                  </label>
                  <label>
                    <span>{c.supportTextLabel}</span>
                    <textarea
                      value={supportText}
                      onChange={(e) => setSupportText(e.target.value)}
                      disabled={!formEditable}
                      maxLength={160}
                      rows={2}
                    />
                  </label>
                </div>
                <div className="composer-fields-row">
                  <label>
                    <span>{c.taglineLabel}</span>
                    <input
                      value={tagline}
                      onChange={(e) => setTagline(e.target.value)}
                      disabled={!formEditable}
                      maxLength={120}
                    />
                  </label>
                  <label>
                    <span>{c.termsCompanyNameLabel}</span>
                    <input
                      value={termsCompanyName}
                      onChange={(e) => setTermsCompanyName(e.target.value)}
                      disabled={!formEditable}
                      maxLength={80}
                    />
                  </label>
                </div>
                <p className="form-hint">{c.taglineHint}</p>
                <p className="form-hint">{c.termsCompanyNameHint}</p>
              </ComposerSection>

              {editingId && (
                <ComposerSection
                  id="reseller-section-branding"
                  title={c.brandingSection}
                  bare
                >
                  <div className="reseller-logo-preview" data-vt-reseller-logo-area>
                    <ResellerLogoThumb
                      profileId={editingId}
                      logoPath={logoPath}
                      hasStagingLogo={hasStagingLogo}
                      version={version}
                      name={operationalName || "لوگو"}
                      variant="preview"
                    />
                  </div>
                  {logoPath ? (
                    <p className="composer-tech-value">
                      <LtrIsolate mono>{logoPath}</LtrIsolate>
                    </p>
                  ) : null}
                  {canManage && profileState !== "archived" && (
                    <div className="form-actions">
                      <label className="btn btn--ghost">
                        {logoBusy ? "در حال آپلود…" : "آپلود / جایگزینی"}
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          hidden
                          disabled={logoBusy}
                          onChange={(e) => {
                            const file = e.target.files?.[0] ?? null;
                            e.target.value = "";
                            void handleLogoUpload(file);
                          }}
                        />
                      </label>
                      {(logoPath || hasStagingLogo) && (
                        <button
                          type="button"
                          className="btn btn--ghost"
                          disabled={logoBusy}
                          onClick={() => void handleLogoRemove()}
                        >
                          حذف لوگو
                        </button>
                      )}
                    </div>
                  )}
                  <p className="field-hint" data-vt-reseller-logo-help>
                    {c.logoUploadHelp}
                  </p>
                </ComposerSection>
              )}

              {editingId && (
                <ComposerSection
                  id="reseller-section-lifecycle"
                  title={c.lifecycleSection}
                  bare
                >
                  <div className="form-actions">
                    {canRead && (
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => void openSnapshots()}
                      >
                        {c.openSnapshots}
                      </button>
                    )}
                    {canPublish && profileState !== "published" && (
                      <button
                        type="button"
                        className="btn btn--primary"
                        onClick={() => setConfirmAction("publish")}
                      >
                        {c.publish}
                      </button>
                    )}
                    {canManage && profileState !== "archived" && (
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => setConfirmAction("archive")}
                      >
                        {c.archive}
                      </button>
                    )}
                  </div>

                  {confirmAction && (
                    <div className="schedule-fieldset" role="group" aria-label="تأیید عملیات">
                      <div className="form-actions">
                        <button
                          type="button"
                          className="btn btn--primary"
                          disabled={actionPending}
                          onClick={() => void runConfirmAction()}
                        >
                          {actionPending ? "در حال انجام…" : "تأیید"}
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost"
                          disabled={actionPending}
                          onClick={() => setConfirmAction(null)}
                        >
                          لغو
                        </button>
                      </div>
                    </div>
                  )}
                </ComposerSection>
              )}

              {editingId && profileState === "published" && canAssign && (
                <ComposerSection
                  id="reseller-section-sites"
                  title={c.sitesSection}
                  bare
                >
                  {activeSites.length === 0 ? null : (
                    <div className="composer-member-checklist">
                      {activeSites.map((site) => (
                        <label key={site.id} className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={selectedSiteIds.includes(site.id)}
                            onChange={() => toggleSite(site.id)}
                          />
                          <span>
                            {site.name} <LtrIsolate mono>({site.code})</LtrIsolate>
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn btn--primary"
                      disabled={assignBusy}
                      onClick={() => void handleAssignSites()}
                    >
                      {assignBusy ? "در حال ذخیره…" : "ذخیرهٔ اتصال سایت‌ها"}
                    </button>
                  </div>
                </ComposerSection>
              )}

              {canManage && formEditable && (
                <StickyFormActions>
                  <button type="submit" className="btn btn--primary" disabled={saving}>
                    {saving ? "در حال ذخیره…" : editingId ? "ذخیره تغییرات" : "ایجاد"}
                  </button>
                  {editingId && (
                    <button type="button" className="btn btn--ghost" onClick={resetForm}>
                      {c.cancel}
                    </button>
                  )}
                </StickyFormActions>
              )}
            </form>
          </ComposerPanel>
        </ComposerWorkspace>
      </div>

      {snapshotsOpen && (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={() => setSnapshotsOpen(false)}
        >
          <div className="modal modal--wide" role="dialog" onClick={(e) => e.stopPropagation()}>
            <h2>اسنپ‌شات‌های برندینگ نمایندگی</h2>
            {snapshotsLoading ? (
              <p className="card__empty">در حال بارگذاری…</p>
            ) : snapshots.length === 0 ? (
              <p className="card__empty">هنوز اسنپ‌شاتی ثبت نشده است.</p>
            ) : (
              <ul className="snapshot-list">
                {snapshots.map((s) => (
                  <li key={s.id}>
                    <div>
                      <strong>
                        نسخه <LtrIsolate mono>{String(s.version)}</LtrIsolate>
                      </strong>
                      {s.rollbackOfSnapshotId && (
                        <span className="state-badge state-badge--scheduled">بازگشت</span>
                      )}
                      <p className="announcement-list__meta">
                        انتشار: <LtrIsolate>{formatKabulDisplay(s.publishedAt)}</LtrIsolate>
                        {" · نگهداشت تا: "}
                        <LtrIsolate mono>{formatKabulDisplay(s.retainUntil)}</LtrIsolate>
                      </p>
                      <p className="announcement-list__meta">
                        {(s.publishedPayload.operationalName as string) ?? "—"}
                      </p>
                    </div>
                    {canPublish && (
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => {
                          setRollbackTarget(s);
                          setRollbackPassword("");
                        }}
                      >
                        بازگشت
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setSnapshotsOpen(false)}
              >
                {c.close}
              </button>
            </div>
          </div>
        </div>
      )}

      {rollbackTarget && (
        <PasswordConfirmModal
          title={`بازگشت به نسخه ${rollbackTarget.version}`}
          description="برندینگ فعلی با محتوای این اسنپ‌شات جایگزین و به‌عنوان نسخهٔ جدید منتشر می‌شود."
          password={rollbackPassword}
          onPasswordChange={setRollbackPassword}
          confirming={rollbackBusy}
          confirmLabel="بازگشت با رمز"
          tone="danger"
          onCancel={() => setRollbackTarget(null)}
          onConfirm={handleRollback}
        />
      )}
    </>
  );
}
